import {
  Card,
  CardContent,
  CardHeader,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { FormikHelpers, useFormikContext } from "formik";
import _ from "lodash";
import { ReactElement, useCallback, useMemo, useState } from "react";
import * as Yup from "yup";

import {
  EditPersonalizedGifMutation,
  EditPersonalizedGifMutationVariables,
} from "~frontend/__generated__/graphql";
import { FormikDialog } from "~frontend/components/forms";
import {
  EDIT_PERSONALIZED_GIF,
  LIST_PERSONALIZED_GIFS,
} from "~frontend/graphql/personalizedGifs";
import { palette } from "~frontend/theme";
import useMutation from "~frontend/util/useMutation";

import { CreateOrEditGifFormActions } from "./CreateOrEditGifFormActions";
import { CustomizeDesignFields } from "./CustomizeDesignFields";
import { CustomizeTextFields } from "./CustomizeTextFields";
import { FormGifPreview } from "./FormGifPreview";
import {
  CreateOrEditGifFormValues,
  CreatomateTemplate,
  PersonalizedGif,
} from "./types";

enum FormStage {
  CUSTOMIZE_TEXT,
  CUSTOMIZE_DESIGN,
  PREVIEW,
}

const formStageLabels: { [key in FormStage]: string } = {
  [FormStage.CUSTOMIZE_TEXT]: "Customize Text",
  [FormStage.CUSTOMIZE_DESIGN]: "Customize Design",
  [FormStage.PREVIEW]: "Preview",
};

export function EditGifDialog({
  personalizedGif,
  onClose,
}: {
  personalizedGif: PersonalizedGif;
  onClose: () => void;
}): ReactElement {
  const [formStage, setFormStage] = useState<FormStage>(
    FormStage.CUSTOMIZE_TEXT
  );

  const [editPersonalizedGif] = useMutation<
    EditPersonalizedGifMutation,
    EditPersonalizedGifMutationVariables
  >(EDIT_PERSONALIZED_GIF, {
    onErrorSnackbarMessage: "Error saving GIF",
    refetchQueries: [LIST_PERSONALIZED_GIFS],
    awaitRefetchQueries: true,
  });

  const handleSubmit = useCallback(
    async (
      values: CreateOrEditGifFormValues,
      formik: FormikHelpers<CreateOrEditGifFormValues>
    ) => {
      const { errors } = await editPersonalizedGif({
        variables: {
          input: {
            personalizedGifId: personalizedGif.id,
            name: values.name!,
            customFields: values.template!.requiredCustomFields.map(field => ({
              ..._.pick(field, "name", "friendlyName"),
              value: values.customFields[field.name],
            })),
            customColors: values.template!.colorVariables.map(
              colorVariable => ({
                ..._.pick(
                  colorVariable,
                  "name",
                  "friendlyName",
                  "modificationKeys"
                ),
                value: values.customColors[colorVariable.name],
              })
            ),
            customLogo: values.template!.logoUrlKey
              ? {
                  key: values.template!.logoUrlKey,
                  url: values.logoUrl ?? null,
                }
              : null,
            contactFieldMetadata: values.template!.requiredContactFields.map(
              field => ({
                ..._.pick(field, "name"),
                fallback: values.customFields[field.name] || "",
              })
            ),
          },
        },
      });

      if (!errors) {
        onClose();
        formik.resetForm();
        setFormStage(FormStage.CUSTOMIZE_TEXT);
      }
    },
    [editPersonalizedGif, personalizedGif, onClose, setFormStage]
  );

  const initialValues: CreateOrEditGifFormValues = useMemo(() => {
    const customFields: Record<string, string> = {};
    for (const customField of personalizedGif.customFields) {
      customFields[customField.name] = customField.value;
    }
    if (personalizedGif.contactFieldMetadata) {
      for (const contactField of personalizedGif.contactFieldMetadata) {
        customFields[contactField.name] = contactField.fallback;
      }
    }

    const customColors: Record<string, string> = {};
    for (const customColor of personalizedGif.customColors) {
      if (customColor.value) {
        customColors[customColor.name] = customColor.value;
      }
    }

    return {
      template: personalizedGif.creatomateTemplate,
      name: personalizedGif.name,
      customFields,
      customColors,
      logoUrl: personalizedGif.customLogo?.url ?? null,
    };
  }, [personalizedGif]);

  return (
    <FormikDialog<CreateOrEditGifFormValues>
      open
      initialValues={initialValues}
      validationSchema={Yup.object().shape({
        template: Yup.object().required(),
        name: Yup.string().required("Name is required"),
        customFields: Yup.object().when(
          "template",
          (template: CreatomateTemplate | null, schema) => {
            if (!template) {
              return schema;
            }
            const shape: Record<string, Yup.AnySchema> = {};
            for (const customField of template.requiredCustomFields) {
              shape[customField.name] = Yup.string()
                .required(`${customField.friendlyName} is required`)
                .max(
                  customField.charLimit,
                  ({ max }) =>
                    `${customField.friendlyName} must be at most ${max} characters`
                );
            }

            // Build up the validation schema for the contact fallback fields
            for (const contactField of template.requiredContactFields) {
              shape[contactField.name] = Yup.string().max(
                contactField.charLimit,
                ({ max }) => `Cannot be longer than ${max} characters`
              );
            }

            return schema.shape(shape);
          }
        ),
        customColors: Yup.object(),
        logoUrl: Yup.string().nullable(),
      })}
      onSubmit={handleSubmit}
    >
      {() => (
        <EditGifDialogInner
          onClose={onClose}
          formStage={formStage}
          onChangeFormStage={setFormStage}
        />
      )}
    </FormikDialog>
  );
}

function EditGifDialogInner({
  onClose,
  formStage,
  onChangeFormStage,
}: {
  onClose: () => void;
  formStage: FormStage;
  onChangeFormStage: (newStage: FormStage) => void;
}) {
  const form = useFormikContext<CreateOrEditGifFormValues>();

  const renderForm = () => {
    switch (formStage) {
      case FormStage.CUSTOMIZE_TEXT:
        return <CustomizeTextFields />;
      case FormStage.CUSTOMIZE_DESIGN:
        return <CustomizeDesignFields />;
      case FormStage.PREVIEW:
        return <FormGifPreview />;
    }
  };

  const renderActions = () => {
    switch (formStage) {
      case FormStage.CUSTOMIZE_TEXT:
        return (
          <CreateOrEditGifFormActions
            onClickNext={() => onChangeFormStage(FormStage.CUSTOMIZE_DESIGN)}
            onClickCancel={onClose}
            nextDisabled={!!form.errors.name || !!form.errors.customFields}
          />
        );
      case FormStage.CUSTOMIZE_DESIGN:
        return (
          <CreateOrEditGifFormActions
            onClickBack={() => onChangeFormStage(FormStage.CUSTOMIZE_TEXT)}
            onClickNext={() => onChangeFormStage(FormStage.PREVIEW)}
            onClickCancel={onClose}
            nextDisabled={!!form.errors.customColors}
          />
        );
      case FormStage.PREVIEW:
        return (
          <CreateOrEditGifFormActions
            onClickBack={() => onChangeFormStage(FormStage.CUSTOMIZE_DESIGN)}
            onClickCancel={onClose}
            onClickSubmit={form.submitForm}
          />
        );
    }
  };

  return (
    <>
      <DialogTitle>Edit Personalized GIF</DialogTitle>
      <DialogContent>
        <Card sx={{ border: "none" }}>
          <CardHeader title={formStageLabels[formStage]} />
          <CardContent
            sx={{
              backgroundColor: palette.lightestGray,
              maxHeight: "500px",
              overflow: "scroll",
            }}
          >
            {renderForm()}
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions>{renderActions()}</DialogActions>
    </>
  );
}
